/**
 * Centralized logging utility for the application
 * Provides environment-aware logging that can be easily controlled
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;
  private isDevelopment: boolean;
  private isConsoleLoggingEnabled: boolean;

  private constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isConsoleLoggingEnabled = process.env.REACT_APP_CONSOLE_LOGGING === 'true';
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR;
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  public setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  public error(message: string, ...args: any[]): void {
    if (this.logLevel >= LogLevel.ERROR && this.isConsoleLoggingEnabled) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  }

  public warn(message: string, ...args: any[]): void {
    if (this.logLevel >= LogLevel.WARN && this.isConsoleLoggingEnabled) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  public info(message: string, ...args: any[]): void {
    if (this.logLevel >= LogLevel.INFO && this.isConsoleLoggingEnabled) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  public debug(message: string, ...args: any[]): void {
    if (this.logLevel >= LogLevel.DEBUG && this.isDevelopment && this.isConsoleLoggingEnabled) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  // Specialized methods for different components
  public visualization3D(message: string, ...args: any[]): void {
    if (this.isDevelopment && this.isConsoleLoggingEnabled) {
      console.log(`[3D-VIZ] ${message}`, ...args);
    }
  }

  public visualization2D(message: string, ...args: any[]): void {
    if (this.isDevelopment && this.isConsoleLoggingEnabled) {
      console.log(`[2D-VIZ] ${message}`, ...args);
    }
  }

  public api(message: string, ...args: any[]): void {
    if (this.isConsoleLoggingEnabled) {
      console.log(`[API] ${message}`, ...args);
    }
  }

  public auth(message: string, ...args: any[]): void {
    if (this.isConsoleLoggingEnabled) {
      console.log(`[AUTH] ${message}`, ...args);
    }
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience exports for common use cases
export const devLog = (message: string, ...args: any[]) => logger.debug(message, ...args);
export const errorLog = (message: string, ...args: any[]) => logger.error(message, ...args);
export const warnLog = (message: string, ...args: any[]) => logger.warn(message, ...args);
export const infoLog = (message: string, ...args: any[]) => logger.info(message, ...args);
